import { z } from 'zod';
import { SlugSchema } from './slug.js';
// Lifecycle maturity, surfaced from the skill frontmatter (`metadata.status`).
// Optional: when absent, the UI shows no badge. Mostly relevant for system skills.
export const SkillStatusSchema = z.enum(['stable', 'beta', 'experimental', 'preview', 'deprecated']);
// Raw ownership. `organization` is reserved for a future iteration.
export const SkillOwnerTypeSchema = z.enum(['user', 'system']);
// The SKILL.md frontmatter contract. Only `name` and `description` are hard
// requirements; metadata fields are advisory — `.catch(fallback)` swallows an
// absent or unknown value so a typo never blocks ingestion. Unknown keys
// (display attributes) are kept by `looseObject`.
/* eslint-disable unicorn/prefer-top-level-await -- `.catch()` is Zod's value fallback, not a Promise handler */
const SkillMetadataSchema = z.looseObject({
    category: z.enum(['tool', 'guide', 'reference']).catch('guide'),
    requires: z.array(z.string()).catch([]),
    status: SkillStatusSchema.optional().catch(undefined)
});
/* eslint-enable unicorn/prefer-top-level-await */
export const SkillFrontmatterSchema = z.object({
    name: SlugSchema,
    description: z.string().min(1, 'description is required'),
    // A SKILL.md with no `metadata:` block is valid — validate it as `{}`, which
    // resolves to the defaults above (no category, no status).
    metadata: z.preprocess(value => value ?? {}, SkillMetadataSchema)
});
// Catalogue (wire) representation of a skill — human-facing and resolved in the
// requesting user's language. The technical SKILL.md fields (`name`,
// `description`) that drive the agent are NOT exposed here.
// A lightweight, displayable reference to another skill (e.g. a dependency).
// Carries `id` so the front can link to it, and `title` so it can label it
// without dealing with slugs.
export const SkillRefSchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string()
});
export const SkillSchema = z.object({
    id: z.string(),
    slug: z.string(),
    // Human display name, localized (fallback: technical `name` = slug).
    title: z.string(),
    // Human summary, localized (fallback: technical LLM `description`).
    description: z.string(),
    // Neutral thematic keys; their labels are localized on the front.
    tags: z.array(z.string()),
    // Neutral, optional — reserved for later use.
    icon: z.string().optional(),
    status: SkillStatusSchema.optional(),
    ownerType: SkillOwnerTypeSchema,
    // Resolved dependencies (from the technical `requires`), displayable and
    // linkable on the front. Unresolvable references are omitted.
    requires: z.array(SkillRefSchema),
    // Effective availability for the current user (delta model).
    available: z.boolean(),
    // The published version the agent reads. Absent only for a skill that has
    // never been published.
    currentVersion: z.number().int().positive().optional(),
    // The owner has saved changes that are not published yet, so the agent is
    // still reading the previous version. Always false for a non-owner.
    hasDraftChanges: z.boolean(),
    updatedAt: z.iso.datetime()
});
// One entry of a skill's publication history. `publishedBy` is deliberately not
// exposed: it is a user id, and nothing on the front needs it yet.
export const SkillVersionSchema = z.object({
    version: z.number().int().positive(),
    contentHash: z.string(),
    publishedAt: z.iso.datetime(),
    // True for the version currently served to the agent.
    current: z.boolean()
});
// Body of `PUT /skills/:id/availability`. Modifying a skill's content happens
// by replacing its bundle, so there is no metadata-edit request schema.
export const SetAvailabilityRequestSchema = z.object({
    available: z.boolean()
});
